import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LINK_TYPES,
  ROLLUP_FNS,
  runFormula,
  FormulaError,
  TITLE_ID,
  updateProperty,
  type DatabaseSchema,
  type PropertyDef,
  type RollupFn,
} from '@nexus/core';
import { useDatabaseList } from '../../db/database';
import { Dialog } from '../Dialog';
import type { Save } from './save';

const field = 'w-full rounded-lg border border-border bg-bg px-2 py-1 text-sm';

/** Settings of relation, backlink, rollup, formula and button properties. */
export function PropertySettings({
  databaseId,
  schema,
  def,
  save,
  open,
  onOpenChange,
}: {
  databaseId: string;
  schema: DatabaseSchema;
  def: PropertyDef;
  save: Save;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const databases = useDatabaseList() ?? [];
  const [draft, setDraft] = useState<PropertyDef>(def);
  const set = (patch: Partial<PropertyDef>) => setDraft((d) => ({ ...d, ...patch }));
  const label = 'flex flex-col gap-1 text-sm';

  const backlinkDb = databases.find((d) => d.id === draft.backlink?.databaseId);
  // Properties elsewhere that can point at this database's rows.
  const pointing = (backlinkDb?.schema.properties ?? []).filter(
    (p) => p.type === 'page' || (p.type === 'relation' && p.relation?.databaseId === databaseId),
  );
  const via = schema.properties.find((p) => p.id === draft.rollup?.via);
  const viaDbId = via?.relation?.databaseId ?? via?.backlink?.databaseId;
  const viaDb = databases.find((d) => d.id === viaDbId);
  const target = viaDb?.schema.properties.find((p) => p.id === draft.rollup?.target);
  const sources = schema.properties.filter((p) => p.type === 'relation' || p.type === 'backlink');

  const preview =
    draft.type === 'formula' && draft.formula?.expression
      ? runFormula(draft.formula.expression, {
          today: new Date().toISOString().slice(0, 10),
          prop: (name) => {
            const n = name.trim().toLowerCase();
            if (['title', 'naam', 'name'].includes(n)) return 'Voorbeeld';
            const p = schema.properties.find((x) => x.name.toLowerCase() === n);
            if (!p) throw new FormulaError(t('db.unknownProperty', { name }));
            return null;
          },
        })
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('db.settingsOf', { name: def.name })}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save((s) => updateProperty(s, draft));
          onOpenChange(false);
        }}
      >
        {draft.type === 'relation' && (
          <label className={label}>
            {t('db.relationTo')}
            <select
              value={draft.relation?.databaseId ?? ''}
              onChange={(e) => set({ relation: { databaseId: e.target.value } })}
              className={field}
              required
            >
              <option value="" />
              {databases.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title || t('page.untitled')}
                </option>
              ))}
            </select>
          </label>
        )}

        {draft.type === 'backlink' && (
          <>
            <label className={label}>
              {t('db.backlinkFrom')}
              <select
                value={draft.backlink?.databaseId ?? ''}
                onChange={(e) => set({ backlink: { databaseId: e.target.value, propertyId: '' } })}
                className={field}
                required
              >
                <option value="" />
                {databases
                  .filter((d) => d.id !== databaseId)
                  .map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.title || t('page.untitled')}
                    </option>
                  ))}
              </select>
            </label>
            <label className={label}>
              {t('db.backlinkVia')}
              <select
                value={draft.backlink?.propertyId ?? ''}
                onChange={(e) =>
                  set({
                    backlink: {
                      databaseId: draft.backlink!.databaseId,
                      propertyId: e.target.value,
                    },
                  })
                }
                className={field}
                required
              >
                <option value="" />
                {pointing.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        {draft.type === 'rollup' && (
          <>
            <label className={label}>
              {t('db.rollupVia')}
              <select
                value={draft.rollup?.via ?? ''}
                onChange={(e) =>
                  set({ rollup: { via: e.target.value, target: TITLE_ID, fn: 'count' } })
                }
                className={field}
                required
              >
                <option value="" />
                {sources.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            {draft.rollup?.via && (
              <>
                <label className={label}>
                  {t('db.rollupTarget')}
                  <select
                    value={draft.rollup.target}
                    onChange={(e) => set({ rollup: { ...draft.rollup!, target: e.target.value } })}
                    className={field}
                  >
                    <option value={TITLE_ID}>{t('db.title')}</option>
                    {(viaDb?.schema.properties ?? [])
                      .filter((p) => !LINK_TYPES.includes(p.type) || p.type === 'page')
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                  </select>
                </label>
                <label className={label}>
                  {t('db.rollupFn')}
                  <select
                    value={draft.rollup.fn}
                    onChange={(e) =>
                      set({ rollup: { ...draft.rollup!, fn: e.target.value as RollupFn } })
                    }
                    className={field}
                  >
                    {ROLLUP_FNS.map((fn) => (
                      <option key={fn} value={fn}>
                        {t(`db.rollupFns.${fn}`)}
                      </option>
                    ))}
                  </select>
                </label>
                {draft.rollup.fn === 'percent_matching' && (
                  <label className={label}>
                    {t('db.rollupMatch')}
                    {target?.options ? (
                      <select
                        value={draft.rollup.match?.value ?? ''}
                        onChange={(e) =>
                          set({
                            rollup: {
                              ...draft.rollup!,
                              match: { op: 'is', value: e.target.value },
                            },
                          })
                        }
                        className={field}
                      >
                        <option value="" />
                        {target.options.map((o) => (
                          <option key={o.name} value={o.name}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        value={draft.rollup.match?.value ?? ''}
                        onChange={(e) =>
                          set({
                            rollup: {
                              ...draft.rollup!,
                              match: { op: 'is', value: e.target.value },
                            },
                          })
                        }
                        className={field}
                      />
                    )}
                  </label>
                )}
              </>
            )}
          </>
        )}

        {draft.type === 'formula' && (
          <>
            <label className={label}>
              {t('db.formula')}
              <textarea
                rows={3}
                value={draft.formula?.expression ?? ''}
                onChange={(e) => set({ formula: { expression: e.target.value } })}
                placeholder='days_until(prop("Deadline"))'
                className={`${field} font-mono`}
                spellCheck={false}
              />
            </label>
            <p className="text-xs text-fg-muted">{t('db.formulaHelp')}</p>
            {preview instanceof FormulaError && (
              <p role="alert" className="text-xs text-danger">
                {preview.message}
              </p>
            )}
          </>
        )}

        {draft.type === 'button' && (
          <p className="text-sm text-fg-muted">{t('db.buttonRecurHelp')}</p>
        )}

        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md px-3 py-1.5 text-sm hover:bg-bg-hover"
          >
            {t('app.cancel')}
          </button>
          <button
            type="submit"
            className="rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg"
          >
            {t('db.saveSettings')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
